import React from "react";
import { Link } from "react-router-dom";
import { Mic, BookOpen, PenLine, Play, ArrowRight } from "lucide-react";
import Signature from "./Signature";

/* Auto-pick internal Link vs external anchor based on href. */
const SmartLink = ({ to, children, ...rest }) => {
  const external = typeof to === "string" && /^https?:\/\//i.test(to);
  if (external) {
    return (
      <a href={to} target="_blank" rel="noopener noreferrer" {...rest}>
        {children}
      </a>
    );
  }
  return (
    <Link to={to} {...rest}>
      {children}
    </Link>
  );
};

/* ── Kind badges ─────────────────────────────────────────────────────── */

const YouTubeMark = () => (
  <span className="inline-flex items-center justify-center w-7 h-5 rounded-[5px] bg-[#E53935]">
    <svg viewBox="0 0 14 14" className="w-2.5 h-2.5" aria-hidden="true">
      <path d="M4 2 L11 7 L4 12 Z" fill="#fff" />
    </svg>
  </span>
);

const KIND_META = {
  podcast:    { Icon: Mic,         label: "Featured Podcast" },
  youtube:    { Icon: YouTubeMark, label: "YouTube Clip", custom: true },
  article:    { Icon: BookOpen,    label: "Article" },
  reflection: { Icon: PenLine,     label: "Reflection" },
};

const KindBadge = ({ kind }) => {
  const meta = KIND_META[kind] || KIND_META.article;
  const Icon = meta.Icon;
  return (
    <div className="inline-flex items-center gap-3 text-[10.5px] tracking-[0.34em] uppercase text-azure-soft whitespace-nowrap">
      {meta.custom ? <Icon /> : <Icon className="w-4 h-4" strokeWidth={1.6} />}
      <span>{meta.label}</span>
    </div>
  );
};

/* ── Decorative audio waveform for featured podcast footer ───────────── */
const Waveform = ({ className = "" }) => (
  <svg viewBox="0 0 280 36" className={className} preserveAspectRatio="none" aria-hidden="true">
    {Array.from({ length: 70 }).map((_, i) => {
      const h = 4 + Math.abs(Math.sin(i * 0.55) * 12) + (i % 6) * 1.4;
      return (
        <rect
          key={i}
          x={i * 4}
          y={18 - h / 2}
          width="1.6"
          height={h}
          fill="#4A8FE5"
          opacity={0.55 + (i % 4) * 0.08}
        />
      );
    })}
  </svg>
);

/* ── Featured podcast card (wide, split text/image) ──────────────────── */
const FeaturedPodcastCard = ({ card, sectionId }) => (
  <SmartLink
    to={card.href}
    data-testid={`featured-card-${sectionId}-main`}
    className="group relative col-span-12 lg:col-span-6 bg-ink-soft border border-white/[0.07] hover:border-azure/40 rounded-[10px] overflow-hidden transition-all duration-700 flex flex-col"
  >
    <div className="grid grid-cols-1 sm:grid-cols-2 flex-1">
      {/* Text column */}
      <div className="relative p-6 lg:p-8 flex flex-col">
        <KindBadge kind={card.kind || "podcast"} />
        <h3 className="mt-12 lg:mt-16 font-serif text-white text-[20px] lg:text-[24px] leading-[1.12] tracking-[-0.012em] max-w-[14ch]">
          {card.title}
        </h3>
        {card.description && (
          <p className="mt-7 text-white/55 text-[13px] leading-[1.85] max-w-[28ch]">
            {card.description}
          </p>
        )}
        <div className="mt-auto" />
      </div>

      {/* Image column */}
      <div className="relative min-h-[320px] sm:min-h-0">
        <img
          src={card.image}
          alt={card.title}
          className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-[1.04]"
        />
        {/* Left fade so the portrait melts into the dark text column */}
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(to right, rgba(16,35,56,0.95) 0%, rgba(16,35,56,0.55) 18%, rgba(16,35,56,0) 45%)",
          }}
        />
      </div>
    </div>

    {/* Footer — play / listen / meta / waveform */}
    <div className="relative flex items-center gap-5 px-8 lg:px-10 py-6 border-t border-white/[0.07] bg-ink-soft/90">
      <span className="inline-flex items-center justify-center w-11 h-11 rounded-full bg-azure/15 border border-azure/40 text-azure group-hover:bg-azure group-hover:text-white transition-colors duration-500">
        <Play className="w-3.5 h-3.5 fill-current" strokeWidth={0} />
      </span>
      <span className="text-[10.5px] tracking-[0.34em] uppercase text-white">
        {card.actionLabel || "Listen Now"}
      </span>
      {card.meta && (
        <span className="text-[10.5px] tracking-[0.30em] uppercase text-white/45">
          {card.meta}
        </span>
      )}
      <div className="hidden sm:block flex-1 h-6 ml-2">
        <Waveform className="w-full h-full" />
      </div>
    </div>
  </SmartLink>
);

/* ── Narrow content card (YouTube / Article / Reflection) ────────────── */
const NarrowCard = ({ card, sectionId, index }) => (
  <SmartLink
    to={card.href}
    data-testid={`featured-card-${sectionId}-${index}`}
    className="group relative col-span-12 sm:col-span-4 lg:col-span-2 bg-ink-soft border border-white/[0.07] hover:border-azure/40 rounded-[10px] overflow-hidden transition-all duration-700 flex flex-col"
  >
    {/* Top kind badge */}
    <div className="px-6 pt-6">
      <KindBadge kind={card.kind} />
    </div>

    {/* Image area */}
    <div className="relative mt-6 mx-6 aspect-[4/5] overflow-hidden rounded-[6px]">
      <img
        src={card.image}
        alt={card.title}
        className="w-full h-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-[1.06]"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-ink-deep/40 via-transparent to-transparent" />
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <span className="w-11 h-11 rounded-full bg-white/10 backdrop-blur-md border border-white/40 flex items-center justify-center transition-transform duration-500 group-hover:scale-110">
          <Play className="w-3.5 h-3.5 text-white fill-current" strokeWidth={0} />
        </span>
      </div>
    </div>

    {/* Title */}
    <div className="px-6 pt-6 pb-7 flex flex-col flex-1">
      <h4 className="font-serif text-white text-[19px] leading-[1.3] tracking-[-0.005em] group-hover:text-azure-soft transition-colors duration-400">
        {card.title}
      </h4>
      <div className="mt-5 h-px w-10 bg-azure-soft/50" />
      <div className="mt-auto pt-7 inline-flex items-center gap-2 text-[10.5px] tracking-[0.34em] uppercase text-azure-soft whitespace-nowrap">
        {card.actionLabel || "Read More"}
        <ArrowRight className="w-3.5 h-3.5 transition-transform duration-400 group-hover:translate-x-1.5" />
      </div>
    </div>
  </SmartLink>
);

/* ── Main section ────────────────────────────────────────────────────── */
const FeaturedSection = ({ section, alt }) => {
  return (
    <section
      data-testid={`featured-section-${section.id}`}
      className={`relative overflow-hidden ${alt ? "bg-ink-deep" : "bg-ink"}`}
    >
      {/* Layered ambient depth */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "radial-gradient(ellipse 70% 55% at 85% 8%, rgba(74,143,229,0.14), transparent 60%), radial-gradient(ellipse 55% 45% at 5% 95%, rgba(74,143,229,0.08), transparent 65%), radial-gradient(ellipse 40% 30% at 50% 40%, rgba(201,169,97,0.04), transparent 70%)",
        }}
      />
      {/* Soft top + bottom vignettes for depth */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute top-0 inset-x-0 h-32"
        style={{ background: "linear-gradient(to bottom, rgba(6,19,37,0.6), transparent)" }}
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute bottom-0 inset-x-0 h-40"
        style={{ background: "linear-gradient(to top, rgba(6,19,37,0.55), transparent)" }}
      />

      <div className="relative max-w-[1440px] mx-auto px-6 lg:px-12 py-16 lg:py-22">
        {/* Header */}
        <div className="mb-10 lg:mb-12">
          <div className="flex items-center gap-4">
            <span className="text-[11px] tracking-[0.34em] uppercase text-azure-soft">
              {section.eyebrow}
            </span>
            <span className="h-px w-10 bg-azure-soft/50" />
          </div>

          <h2 className="mt-9 font-serif text-white text-[32px] sm:text-[44px] lg:text-[56px] leading-[1.02] tracking-[-0.02em]">
            {section.headlineA}
          </h2>
          <h2 className="font-serif italic text-azure-soft text-[32px] sm:text-[44px] lg:text-[56px] leading-[1.02] tracking-[-0.02em]">
            {section.headlineB}
          </h2>

          {/* Intro + View-all row */}
          <div className="mt-14 lg:mt-16 grid lg:grid-cols-12 gap-10 lg:gap-16 items-end">
            <p className="lg:col-span-7 text-white/65 text-[15px] leading-[1.95] max-w-2xl">
              {section.intro}
            </p>
            <div className="lg:col-span-5 lg:flex lg:justify-end">
              <Link
                to={section.viewAllHref}
                data-testid={`featured-view-all-${section.id}`}
                className="group inline-flex items-center gap-3 text-[11px] tracking-[0.34em] uppercase text-azure-soft hover:text-azure border-b border-azure-soft/60 hover:border-azure pb-2.5 transition-colors"
              >
                View All {section.viewAllLabel || "Content"}
                <ArrowRight className="w-3.5 h-3.5 transition-transform duration-400 group-hover:translate-x-1.5" />
              </Link>
            </div>
          </div>
        </div>

        {/* Card row — asymmetric: 1 wide + 3 narrow */}
        <div className="grid grid-cols-12 gap-5 lg:gap-6 items-stretch">
          <FeaturedPodcastCard card={section.featured} sectionId={section.id} />
          {section.cards.map((c, i) => (
            <NarrowCard key={`${section.id}-${i}`} card={c} sectionId={section.id} index={i} />
          ))}
        </div>

        {/* Closing pull-quote */}
        {section.quote && (
          <div className="mt-16 lg:mt-22 grid lg:grid-cols-12 gap-10 items-end">
            <div className="lg:col-span-8 flex gap-7">
              <span
                className="font-serif text-azure-soft text-[110px] leading-[0.8] -mt-4 select-none"
                aria-hidden="true"
              >
                “
              </span>
              <div className="pt-2 border-l border-azure-soft/30 pl-7">
                <p className="font-serif text-white text-[19px] lg:text-[24px] leading-[1.4] tracking-[-0.01em]">
                  {section.quote.lineA}
                </p>
                <p className="font-serif italic text-azure-soft text-[19px] lg:text-[24px] leading-[1.4] tracking-[-0.01em]">
                  {section.quote.lineB}
                </p>
              </div>
            </div>
            <div className="lg:col-span-4 lg:text-right">
              <div className="lg:flex lg:justify-end">
                <Signature className="h-16" align="right" />
              </div>
              <div className="mt-4 text-[10.5px] tracking-[0.34em] uppercase text-white/45">
                {section.quote.caption}
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default FeaturedSection;
